import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Accordion, AccordionSummary, AccordionDetails, Chip, IconButton,
  CircularProgress, Alert, MenuItem,
} from '@mui/material';
import { Add, Edit, Delete, ExpandMore } from '@mui/icons-material';
import { accountingApi, fundTypeApi } from '../../services/api';
import { Account, FundType } from '../../types';

const typeOrder = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];
const typeColors: Record<string, string> = { Asset: 'primary', Liability: 'warning', Equity: 'success', Revenue: 'info', Expense: 'error' };
const accountTypes = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

const ChartOfAccounts: React.FC = () => {
  const [grouped, setGrouped] = useState<Record<string, Account[]>>({});
  const [fundTypes, setFundTypes] = useState<FundType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Account | null>(null);
  const [saving, setSaving] = useState(false);

  const [accountCode, setAccountCode] = useState('');
  const [accName, setAccName] = useState('');
  const [accDesc, setAccDesc] = useState('');
  const [accType, setAccType] = useState('Asset');
  const [normalBalance, setNormalBalance] = useState('Debit');
  const [fundTypeId, setFundTypeId] = useState('');
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [accRes, ftRes] = await Promise.all([
        accountingApi.getChartOfAccounts(),
        fundTypeApi.getAll(),
      ]);
      setGrouped(accRes.data || {});
      setFundTypes(Array.isArray(ftRes.data) ? ftRes.data : []);
    } catch { setError('Failed to load chart of accounts.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => {
    setEditingItem(null);
    setAccountCode(''); setAccName(''); setAccDesc('');
    setAccType('Asset'); setNormalBalance('Debit');
    setFundTypeId(fundTypes[0]?.id ? String(fundTypes[0].id) : '');
    setFormError(''); setDialogOpen(true);
  };

  const openEdit = (a: Account) => {
    setEditingItem(a);
    setAccountCode(a.accountCode); setAccName(a.name); setAccDesc(a.description || '');
    setAccType(a.type); setNormalBalance(a.normalBalance);
    setFundTypeId(String(a.fundTypeId));
    setFormError(''); setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!accountCode.trim() || !accName.trim()) { setFormError('Code and Name are required.'); return; }
    setSaving(true); setFormError('');
    try {
      const payload = {
        accountCode: accountCode.trim(), name: accName.trim(), description: accDesc.trim(),
        type: accType, normalBalance, fundTypeId: Number(fundTypeId),
      };
      if (editingItem) await accountingApi.updateAccount(editingItem.id, payload);
      else await accountingApi.createAccount(payload);
      await fetchData();
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err.response?.data?.title || err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (a: Account) => {
    if (!window.confirm(`Delete account "${a.accountCode} - ${a.name}"?`)) return;
    try {
      await accountingApi.deleteAccount(a.id);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete.');
    }
  };

  const allAccounts = Object.values(grouped).flat();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Chart of Accounts</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add Account</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : typeOrder.map(type => {
        const accounts = grouped[type];
        if (!accounts || accounts.length === 0) return null;
        return (
          <Accordion key={type} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Chip label={`${type} (${accounts.length})`} color={(typeColors[type] as any) || 'default'} size="small" sx={{ fontWeight: 600 }} />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Normal Balance</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fund Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accounts.map(acc => (
                      <TableRow key={acc.id} hover>
                        <TableCell>{acc.accountCode}</TableCell>
                        <TableCell>{acc.name}</TableCell>
                        <TableCell>
                          <Chip label={acc.normalBalance} size="small" color={acc.normalBalance === 'Debit' ? 'info' : 'warning'} variant="outlined" />
                        </TableCell>
                        <TableCell>{acc.fundTypeName}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEdit(acc)} color="primary"><Edit /></IconButton>
                          <IconButton size="small" onClick={() => handleDelete(acc)} color="error"><Delete /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}
      {allAccounts.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No accounts found. Click "Add Account" to create one.</Typography>
        </Paper>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Account' : 'Add Account'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <TextField label="Account Code" fullWidth margin="normal" value={accountCode} onChange={e => setAccountCode(e.target.value)} required disabled={saving} />
          <TextField label="Name" fullWidth margin="normal" value={accName} onChange={e => setAccName(e.target.value)} required disabled={saving} />
          <TextField label="Description" fullWidth margin="normal" value={accDesc} onChange={e => setAccDesc(e.target.value)} multiline rows={2} disabled={saving} />
          <TextField label="Type" select fullWidth margin="normal" value={accType} onChange={e => setAccType(e.target.value)} disabled={saving}>
            {accountTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="Normal Balance" select fullWidth margin="normal" value={normalBalance} onChange={e => setNormalBalance(e.target.value)} disabled={saving}>
            <MenuItem value="Debit">Debit</MenuItem>
            <MenuItem value="Credit">Credit</MenuItem>
          </TextField>
          <TextField label="Fund Type" select fullWidth margin="normal" value={fundTypeId} onChange={e => setFundTypeId(e.target.value)} disabled={saving}>
            {fundTypes.map(ft => <MenuItem key={ft.id} value={String(ft.id)}>{ft.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChartOfAccounts;
